/**
 * Resolve shared learning-item references (FAME-237) to displayable card
 * metadata, in one batched `filter[ids]` lookup on `GET /api/v3/chat/shared_items`
 * (FAME-453). Per-caller authorized: an id the caller cannot open is absent from
 * the map, which the card renders as its no-access state.
 *
 * `byId` is `undefined` while the lookup is in flight — the card shows its
 * resolving state until then. Not cached forever like handle resolution: access
 * and title can change, so this refetches on the default policy.
 *
 * `isError` when the lookup failed (network, or the 404 when chat is disabled).
 * The caller renders the fallback card — the sender's deep link — rather than
 * spinning forever, since retrying alone would never resolve it.
 */
import { v3 } from "backoffice-api"
import { useMemo } from "react"
import type { ProductTypeIdentifier } from "../../graphql"
import type { SharedItem, SharedItemRef } from "../types"

type SharedItems = {
  byId: Map<string, SharedItem> | undefined
  isError: boolean
}

export function useSharedItems(refs: SharedItemRef[]): SharedItems {
  const ids = refs.map((ref) => ref.id)

  const { data, isError } = v3.getChatSharedItems.useQuery(
    [{ "filter[ids]": ids.join(",") }],
    { enabled: ids.length > 0 }
  )

  const byId = useMemo(() => {
    if (data === undefined) return undefined

    return new Map(
      data.data.map((item) => [
        item.id,
        {
          id: item.id,
          title: item.title,
          thumbnail: item.thumbnail,
          productType: item.product_type_identifier as ProductTypeIdentifier,
        },
      ])
    )
  }, [data])

  return { byId, isError }
}
